import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import IMAGES from "@/public/assets/img";
import Head from "next/head";
import { WEB_URL } from "@/config/endpoints";
import corporateLoan from "@/config/corporateLoan_config";
import AccordianFaq from "@/component/shared/accordianfaq";
import HighValueLoan from "@/component/corporateLoan/highValue";
import LapSolutions from "@/component/corporateLoan/lapSolutions";
import IndustryUseCases from "@/component/corporateLoan/industryCases";
import AcceptedCollaterals from "@/component/corporateLoan/acceptedCollaterals";
import WhyCredlixSection from "@/component/corporateLoan/whyCredlixSection";
import { generateFaqSchema } from "@/component/shared/faqSchema";
import TrustedSection from "@/component/dealerFinancing/trustedSection";

const CorporateLoan = () => {
  const router = useRouter();
  const [mWidth, setMWidth] = useState(0);
  const faqSchema = generateFaqSchema(corporateLoan.Faq);
  const sectionRef = useRef(null);

  const scrollToSection = () => {
    if (sectionRef.current) {
      const headerOffset = 110;
      const elementPosition = sectionRef.current.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const handleWindowResize = () => {
    setMWidth(window.innerWidth);
  };

  useEffect(() => {
    handleWindowResize();
    window.addEventListener("load", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  return (
    <main className="pt-[110px]">
      <Head>
        <title>{corporateLoan.seoData.title}</title>
        <meta name="description" content={corporateLoan.seoData.description} />
        {/* OpenGraph tags */}
        <meta property="og:title" content={corporateLoan.seoData.title} />
        <meta
          property="og:description"
          content={corporateLoan.seoData.description}
        />
        <meta property="og:image" content={IMAGES.HOMEIMG.bigLogoNew.src} />
        <meta property="og:url" content={WEB_URL + router.asPath} />
        <link rel="canonical" href={WEB_URL + router.asPath} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqSchema),
          }}
        />
      </Head>
      <section
        className={`relative bg-no-repeat sm:bg-center bg-cover bg-bottom min-h-[450px] sm:min-h-[270px]`}
      >
        {mWidth > 768 ? (
          <Image
            priority={true}
            src={IMAGES.corporateLoan.bgCorporateDesktop}
            width={1366}
            height={550}
            className="absolute inset-0 max-w-none w-full h-full"
            alt="Banner Desktop"
          />
        ) : (
          <Image
            priority={true}
            src={IMAGES.corporateLoan.bgCorporateMb}
            width={767}
            height={451}
            className="absolute inset-0 max-w-none w-full h-full"
            alt="Banner Mobile"
          />
        )}
        <div className="container xm:px-2 relative flex justify-between items-center sm:flex-col gap-2 py-14">
          <div className="w-3/5 sm:w-full">
            <h1
              className="text-brand text-heading6 sm:text-textxl mb-4 sm:mb-2"
              dangerouslySetInnerHTML={{ __html: corporateLoan.bannerHeading }}
            />
            <h2 className="bg-[#E3A003] w-fit text-center py-xsp px-4 sm:px-1 rounded-lg mb-2 text-[22px] sm:text-xs text-white font-medium md:text-base2">
              {corporateLoan.subheading}
            </h2>
            <p className="text-sm text-baseBlack pr-6 sm:pr-0 md:pr-0">{corporateLoan.paraLoan}</p>
            <button className="mt-5 flex gap-2 bg-gradient-to-r from-[#022B5C] to-[#0363C4] shadow-lg transition-all duration-300 hover:from-[#0363C4] hover:to-[#022B5C] text-white px-5 py-2 rounded-full text-base sm:py-1 font-normal whitespace-nowrap"
            onClick={scrollToSection}>
              {corporateLoan.btntext}
              <svg
                xmlns="https://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="brand"
                width="24"
                height="24"
                className="transition-transform duration-200 transform group-hover:translate-x-[20px] fill-current text-white"
              >
                <path d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z"></path>
              </svg>
            </button>
          </div>
          <div className="w-2/6 sm:w-full pt-5 sm:pt-2">
            <Image
              src={IMAGES.corporateLoan.building}
              alt="Corporate Loan"
              width={387}
              height={406}
              className="w-full h-auto"
            />
          </div>
        </div>
      </section>
      <TrustedSection trustData={corporateLoan.benefits}/>
      <HighValueLoan data={corporateLoan.highValueLoan} />
      <LapSolutions data={corporateLoan.lapSoulations} />
      <AcceptedCollaterals data={corporateLoan.acceptedCollaterals} />
      <WhyCredlixSection HowItWorks={corporateLoan.HowItWorks} />
      <IndustryUseCases data={corporateLoan.industryCases} />

      <section className="bg-white py-sp-100 sm:py-sp-big">
        <div className="container px-3 xm:px-2">
          <div>
            <h3 className="mb-6 font-medium text-center text-lg text-secondary">
              {corporateLoan.FaqHead}
            </h3>
          </div>
          <div className="w-full text-left bg-white">
            <div className="mx-auto max-w-[1100px] sm:w-auto">
              <AccordianFaq faqData={corporateLoan.Faq} />
            </div>
          </div>
        </div>
      </section>
      <div ref={sectionRef}></div>
    </main>
  );
};

export default CorporateLoan;
